<template>
  <div class="universal-search full-width" @click.stop>
    <div ref="inputBlur" class="q-focus-helper" tabindex="-1"></div>
    <SearchInput
      @blur="onBlur"
      @clear="onClear"
      @click="performSearch"
      @focus="onFocus"
      @keydown="onInputKeyDown"
      @resize="({ width }) => (searchInputWidth = width)"
      @update:model-value="performSearch"
      @update:search-type="onSearchTypeChange"
    />
    <SearchResultsList
      :search-input-width="searchInputWidth"
      :show="(!$q.screen.gt.xs && showResultsOnMobile) || (isInFocus && (!isFilter || !isFilterRoute))"
      @click="handleClickedResult"
      @details-click="handleClickedResult"
      @hide="onSearchResultsHide"
      @live-map-click="onLiveMapClick"
      @overview-click="onOverviewClick"
      @show="addOutsideClickListener"
    />
  </div>
</template>

<script setup lang="ts">
import _debounce from 'lodash/debounce';
import { useQuasar } from 'quasar';
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import SearchInput from 'components/universal-search/SearchInput.vue';
import SearchResultsList from 'components/universal-search/SearchResultsList.vue';
import { useOutsideClick } from 'src/composables/outside-click';
import Bounds from 'src/models/Bounds';
import { SEARCH_TYPES } from 'src/services/constants';
import MapService from 'src/services/map';
import { navigateToUrl } from 'src/services/navigation';
import { getEquivalentRoute } from 'src/services/router';
import { setTimeoutPromise } from 'src/services/setTimeout';
import type { SearchItem as StoreSearchItem } from 'src/types/search-store';

type ExecuteActionType =
  | 'live-map'
  | 'live-map-feature'
  | 'overview'
  | 'place'
  | 'user'
  | 'asset'
  | 'vehicle'
  | 'address';

type UniversalSearchItem = Omit<StoreSearchItem, 'type'> & {
  bounds?: [number, number, number, number];
  hasLocation?: boolean;
  isMappable?: boolean;
  liveMapKey?: string;
  tripsRoute?: string;
  type: ExecuteActionType;
};

const emit = defineEmits(['blur', 'focus', 'update:search-type']);

defineExpose({ performSearch });

const store = useStore();
const route = useRoute();
const $q = useQuasar();
useOutsideClick('search-input', () => {
  emit('blur');
});
const { addOutsideClickListener, removeOutsideClickListener } = useOutsideClick(
  'universalSearch',
  () => {
    onBlur();
  },
  false
);

const inputBlur = useTemplateRef('inputBlur');
const isInFocus = ref(false);
const previousTerm = ref<string | null>(null);
const showResultsOnMobile = ref(false);
const searchInputWidth = ref(0);

const isFilterRoute = computed(() => store.state.app.isFilterRoute);
const isLiveMapRoute = computed(() => store.state.app.isLiveMapRoute);
const isOnDetailsPage = computed(() => store.state.app.isOnDetailsPage);

const searchedTerms = computed(() => store.state.search.searchedTerms || '');
const searchInputInFocus = computed(() => store.state.search.searchInputInFocus);
const searchType = computed(() => store.state.search.searchType);
const showPlaces = computed(() => store.state.map.showPlaces);
const allOptionsIndexed = computed(() => store.getters['search/allOptionsIndexed'] as UniversalSearchItem[]);
const selectedResult = computed(() => store.getters['search/selectedResult'] as UniversalSearchItem | undefined);

const onLiveMap = computed(() => ['map', 'map-selected'].includes(route.name as string));
const isFilter = computed(() => searchType.value === SEARCH_TYPES.FILTER);

function setMapSearchTerms(terms: string) {
  return store.dispatch('map/setSearchTerms', terms);
}

function selectPreviousResult() {
  return store.dispatch('search/selectPreviousResult');
}

function setIsSearching(value: boolean) {
  return store.dispatch('search/setIsSearching', value);
}

function updateFilterTermsAction(terms: string) {
  return store.dispatch('search/updateFilterTerms', terms);
}

function setKeywordSearchTerms(terms: string) {
  return store.dispatch('search/setSearchedTerms', terms);
}

function onSearchResultsHide() {
  showResultsOnMobile.value = false;
  removeOutsideClickListener();
}

const debouncedSearch = _debounce(async (terms: string) => {
  await store.dispatch('search/updateSearchTerms', terms);
  await setIsSearching(false);
}, 500);

onBeforeUnmount(() => {
  debouncedSearch.cancel();
  removeOutsideClickListener();
});

async function executeAction(type: ExecuteActionType | undefined, result: UniversalSearchItem) {
  showResultsOnMobile.value = false;

  if (!type) {
    return;
  }

  const { liveMapKey } = result;

  switch (type) {
    case 'live-map':
      if (liveMapKey) {
        navigateToUrl(`/selected/${liveMapKey}`);
      } else if (!onLiveMap.value) {
        navigateToUrl('/');
      }
      break;
    case 'live-map-feature':
      showOnLiveMap(result);
      return;
    case 'overview':
      navigateToUrl({ name: result.type, params: { key: result.key } });
      break;
    case 'place':
      navigateToUrl(`/places/${result.key}`);
      break;
    case 'user':
      navigateToUrl(`/users/${result.key}`);
      break;
    case 'asset':
    case 'vehicle':
      if (isOnDetailsPage.value) {
        const routeName = getEquivalentRoute(route.name as string, type, result.tripsRoute);
        navigateToUrl({ name: routeName, params: { key: result.key } });
      } else {
        navigateToUrl({ name: result.tripsRoute, params: { key: result.key } });
      }
      break;
    case 'address':
      navigateToUrl('/');
      await setMapSearchTerms('');
      await setTimeoutPromise(500);
      await setMapSearchTerms(searchedTerms.value);
      await setKeywordSearchTerms('');
      return;
    default:
      break;
  }

  await store.dispatch('search/setSearchType', SEARCH_TYPES.KEYWORD);
  void store.dispatch('search/saveHistory', result);
  inputBlur.value?.focus();
}

function handleClickedResult(result: UniversalSearchItem) {
  void executeAction(result.type, result);
}

async function onBlur() {
  await setTimeoutPromise(200);
  isInFocus.value = searchInputInFocus.value;
  await store.dispatch('search/resetResultSelection');
  emit('blur');
}

function onClear() {
  void performSearch('');
}

async function onFocus(terms: string) {
  previousTerm.value = '';
  void performSearch(terms);
  await setTimeoutPromise(200);
  isInFocus.value = true;

  if (searchType.value === SEARCH_TYPES.KEYWORD) {
    showResultsOnMobile.value = true;
  }

  emit('focus');
}

function onInputKeyDown(event: KeyboardEvent) {
  const result = selectedResult.value || allOptionsIndexed.value?.[0];

  switch (event.key) {
    case 'ArrowUp':
      event.preventDefault();
      void selectPreviousResult();
      break;
    case 'ArrowDown':
      event.preventDefault();
      void store.dispatch('search/selectNextResult');
      break;
    case 'Enter':
      if (isFilter.value) {
        return;
      }
      if (result) {
        event.preventDefault();
        if (isLiveMapRoute.value && result.isMappable) {
          onLiveMapClick(result);
          return;
        }
        handleClickedResult(result);
      }
      break;
    default:
      break;
  }
}

function onLiveMapClick(result: UniversalSearchItem) {
  void executeAction('live-map-feature', result);
}

function onOverviewClick(result: UniversalSearchItem) {
  void executeAction('overview', result);
}

function onSearchTypeChange(value: string) {
  showResultsOnMobile.value = searchType.value === SEARCH_TYPES.KEYWORD ? true : false;
  performSearch(value);
}

async function performSearch(rawTerms: string) {
  const terms = rawTerms.toLowerCase();

  if (isFilterRoute.value && isFilter.value) {
    await updateFilterTermsAction(terms);
    return;
  }

  if (terms === previousTerm.value) {
    return;
  }

  previousTerm.value = terms;
  void setKeywordSearchTerms(terms);

  if (terms.length >= 2) {
    void setIsSearching(true);
    debouncedSearch(terms);
    return;
  }

  await store.dispatch('search/setSearchItems', []);
}

function showOnLiveMap(result: UniversalSearchItem) {
  const { bounds, hasLocation, type } = result;

  if (!hasLocation) {
    $q.notify({
      color: 'electric-yellow-light',
      textColor: 'brown-10',
      icon: 'location_off',
      message: 'Cannot select this vehicle because it does not have a location',
    });
    return;
  }

  const focusIn = () => {
    if (bounds) {
      if (type === 'place' && !showPlaces.value) {
        void store.dispatch('map/togglePlaces');
      }
      void store.dispatch('map/fitToBounds', new Bounds(bounds));
    }
  };

  if (onLiveMap.value) {
    focusIn();
  } else {
    MapService.onReady(() => {
      MapService.once('focus', () => {
        focusIn();
      });
    });
  }

  void executeAction('live-map', result);
}
</script>

<style lang="scss" scoped>
.universal-search {
  max-width: 600px;
  min-width: 200px;
}
</style>
